import React, { useEffect, useState } from 'react'

import { ArrowLeft } from 'lucide-react'
import { UseFormReturn } from 'react-hook-form'

import { StatusUpgrade } from '@/core/api/entities/account/statusUpgrade'
import { Subscription } from '@/core/api/entities/account/subscription'
import { User } from '@/core/api/entities/user'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ZoneRequestDTO } from '@/features/zones/Create/Create'
// Import step components
import { NetworkConfigStep } from '@/features/zones/Create/steps/NetworkConfigStep'
import NewLocationStep from '@/features/zones/Create/steps/NewLocationStep'
import { Steps as StepsEnum } from '@/features/zones/Create/steps/Steps'
import ZoneSetupStep from '@/features/zones/Create/steps/ZoneSetupStep'
import { Button } from '@/shared/components/atoms/Button'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'

import ConfirmationStep from './steps/ConfirmationStep'

interface CreateZoneUIProps {
	currentStep: number
	totalSteps: number
	zoneType: 'single' | 'multiple'
	setZoneType: (zoneType: 'single' | 'multiple') => void
	form: UseFormReturn<ZoneRequestDTO>
	onFormDataChange: (data: Partial<ZoneRequestDTO>) => void
	onSubmit: (data: ZoneRequestDTO) => void
	onNext: () => void
	onBack: () => void
	onCancel: () => void
	canGoNext: boolean
	isCreating: boolean
	isLoadingStatus: boolean
	accountUpgradeStatus: StatusUpgrade | undefined
	user: User | null
	success: boolean
	currentSubscription: Subscription | null
}

export const CreateZoneUI: React.FC<CreateZoneUIProps> = ({
	currentStep,
	totalSteps,
	zoneType,
	setZoneType,
	form,
	onFormDataChange,
	onSubmit,
	onNext,
	onBack,
	onCancel,
	canGoNext,
	isCreating,
	isLoadingStatus,
	accountUpgradeStatus,
	user,
	currentSubscription,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const {
		handleSubmit,
		watch,
		control,
		formState: { errors },
	} = form

	// Determine field prefix for NetworkConfigStep based on location type
	const isNewLocation = watch('store_id') === -1
	const networkFieldPrefix = isNewLocation ? 'store' : 'box.connection_settings'

	// Steps configuration (4 steps total)
	const steps = [
		{
			title: t('create.steps.zoneSetup'),
			content: (
				<ZoneSetupStep
					user={user}
					accountUpgradeStatus={accountUpgradeStatus}
					isLoadingStatus={isLoadingStatus}
					form={form}
					onZoneTypeSelect={setZoneType}
					selectedZoneType={zoneType}
					onFormDataChange={onFormDataChange}
				/>
			),
		},
		{
			title: t('create.steps.locationInfo'),
			content: (
				<NewLocationStep form={form} onFormDataChange={onFormDataChange} />
			),
		},
		{
			title: t('create.steps.networkConfig'),
			content: (
				<NetworkConfigStep
					control={control}
					errors={errors}
					fieldPrefix={
						networkFieldPrefix as 'store' | 'box.connection_settings'
					}
				/>
			),
		},
		{
			title: t('create.steps.confirmation'),
			content: (
				<ConfirmationStep
					form={form}
					currentSubscription={currentSubscription}
					accountUpgradeStatus={accountUpgradeStatus}
				/>
			),
		},
	]

	const onFormSubmit = (data: any) => {
		onSubmit(data as ZoneRequestDTO)
	}

	const handleBack = () =>
		currentStep === StepsEnum.ZoneSettings ? onCancel() : onBack()

	// Mobile: show header in app bar. Desktop: show in page.
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window !== 'undefined'
			? window.matchMedia('(min-width: 768px)').matches
			: true
	)
	useEffect(() => {
		const mq = window.matchMedia('(min-width: 768px)')
		const onChange = () => setIsDesktop(mq.matches)
		mq.addEventListener('change', onChange)
		return () => mq.removeEventListener('change', onChange)
	}, [])

	// Ensure currentStep is within bounds
	const safeCurrentStep = Math.min(currentStep, steps.length - 1)
	const currentStepData = steps[safeCurrentStep]

	const pageTitle =
		currentStep === StepsEnum.Location ||
		currentStep === StepsEnum.NetworkConfig
			? currentStepData?.title
			: t('create.title')

	if (!currentStepData) {
		return null
	}

	return (
		<PageLayout
			requireAuth={true}
			{...(isDesktop
				? {
						title: pageTitle,
						backButton: { show: true, onClick: handleBack },
					}
				: {})}
			topAppBarLeftContent={
				!isDesktop ? (
					<div className="flex items-center gap-3 min-w-0">
						<button
							onClick={handleBack}
							className="flex-shrink-0 w-9 h-9 rounded-full bg-background/30 text-foreground flex items-center justify-center font-semibold text-base cursor-pointer transition-colors hover:bg-background/20"
							aria-label={tCommon('actions.back')}
						>
							<ArrowLeft className="h-5 w-5" />
						</button>
						<h1 className="text-lg font-normal text-foreground truncate min-w-0">
							{pageTitle}
						</h1>
					</div>
				) : undefined
			}
			contentClassName="p-0"
		>
			<div className="px-4 md:px-8 py-6 min-h-[calc(100vh-300px)] max-w-[700px] overflow-x-hidden overflow-y-auto">
				{currentStepData.content}
			</div>

			<div className="border-t border-border px-4 md:px-8 py-4">
				<div className="flex flex-col-reverse md:flex-row items-center gap-2">
					<Button
						onClick={currentStep === StepsEnum.ZoneSettings ? onCancel : onBack}
						variant="outline"
						className="w-full md:w-auto rounded-full cursor-pointer"
					>
						{currentStep === StepsEnum.ZoneSettings
							? tCommon('actions.cancel')
							: tCommon('actions.back')}
					</Button>

					{currentStep === totalSteps - 1 ? (
						<Button
							onClick={handleSubmit(onFormSubmit)}
							variant="default"
							disabled={isCreating}
							className="w-full md:w-auto rounded-full cursor-pointer"
						>
							{isCreating
								? tCommon('actions.loading')
								: tCommon('actions.confirm')}
						</Button>
					) : (
						<Button
							onClick={onNext}
							variant="default"
							className="w-full md:w-auto rounded-full cursor-pointer"
							disabled={!canGoNext}
						>
							{tCommon('actions.continue')}
						</Button>
					)}
				</div>
			</div>
		</PageLayout>
	)
}
